/**
 * Chip presentation rules, in one place because three components render chips
 * (AccessChip, UserChip, MatrixView) and they must not drift.
 *
 * The colour axis answers "which application", the badge answers "how long":
 *
 *   - `imp-ai`        teal fill      — Ennabl AI
 *   - `imp-decisions` violet fill    — Decisions
 *   - `data-access`   NO fill, outline only
 *
 * Data Access being the only outlined chip is deliberate. It grants
 * STS-AssumeRole into an AWS account, and the same account may be used by either
 * application, so it carries no application colour — it reads as app-neutral
 * infrastructure between the two coloured ones. Do not give it an app dimension.
 */
import {ChipKind} from './aggregate';
import {isAiAccount} from './ai-accounts';

/** CSS class carrying the chip's application colour. */
export function chipKindClass(kind: ChipKind): string {
    switch (kind) {
        case 'imp-ai':
            return 'k-imp-ai';
        case 'imp-decisions':
            return 'k-imp-dec';
        case 'data-access':
            return 'k-data';
    }
}

/**
 * Extra class marking a Data Access chip whose target is an internal Ennabl AI
 * account (green-tinted border + faint fill).
 *
 * This marks the ACCOUNT as internal — it does NOT mean the grant belongs to
 * Ennabl AI. An impersonation chip can never carry it, because an AI account is
 * not a valid impersonation resource.
 */
export function chipInternalClass(kind: ChipKind, resource: string): string {
    return kind === 'data-access' && isAiAccount(resource) ? 'internal' : '';
}

/**
 * Legend swatch class for a chip kind. The MatrixView legend shows one swatch in
 * the active application's colour, so it reuses the same three-way mapping the
 * chips do rather than a parallel imp/data binary.
 */
export function legendSwatchClass(kind: ChipKind): string {
    switch (kind) {
        case 'imp-ai':
            return 'imp-ai';
        case 'imp-decisions':
            return 'imp-dec';
        case 'data-access':
            return 'data';
    }
}

/** Two-part label for a capability: the application, then the access type. */
export interface KindLabel {
    /** Application name. `data-access` is app-neutral, so it reads as the platform. */
    app: string;
    type: string;
}

/**
 * Labels for the three capabilities. Ennabl AI first, Decisions last — that
 * order holds everywhere: tabs, the access-type list in the modal, any legend.
 */
export const KIND_LABEL: Record<ChipKind, KindLabel> = {
    'imp-ai': {app: 'Ennabl AI', type: 'Impersonation'},
    'data-access': {app: 'AWS', type: 'Data Access'},
    'imp-decisions': {app: 'Decisions', type: 'Impersonation'},
};

/** The tab / heading order. Ennabl AI, then app-neutral Data Access, then Decisions. */
export const KIND_ORDER: readonly ChipKind[] = ['imp-ai', 'data-access', 'imp-decisions'];

/** One-line heading, e.g. `Ennabl AI · Impersonation`. */
export function kindHeading(kind: ChipKind): string {
    const {app, type} = KIND_LABEL[kind];
    return `${app} · ${type}`;
}
